import { SubscriptionStatusDTO, WorkspaceMemberProfile, WorkspaceRole, WorkspaceSeatType } from 'models/user'
import { NewWorkspaceDTO, UserAsMemberUsingEmailDTO, WorkspaceAddMembersResultDTO } from 'models/workspaces'
import { EntityKeys, QueryType, getMutation } from 'utils/reactQuery'

import { Nullable } from 'models/helpers'
import { SignedURLDTO } from 'models/visuals'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { RoleMimetype } from 'constants/API'
import { useAPI } from 'utils/API/useApi'
import { useGetSubscriptionCheckoutLink } from './subscriptions.query'

enum Endpoints {
  UPDATE_WORKSPACE_NAME = '/workspace/{workspaceId}',
  CREATE_WORKSPACE = '/workspace/',
  CHANGE_WORKSPACE_MEMBER_ROLE = '/workspace/{workspaceId}/changeMemberRole',
  REMOVE_AND_TRANSFER_MEMBER = '/workspace/{workspaceId}/member/remove-and-transfer',
  RESEND_WORKSPACE_INVITATION = '/workspace/{workspaceId}/resend-invitation/{invitationId}', // under FF allowPendingInvitations
  WORKSPACE_INVITATION = '/workspace/{workspaceId}/invitation/{invitationId}', // under FF allowPendingInvitations
  ADD_WORKSPACE_MEMBERS = '/workspace/{workspaceId}/members',
  GET_WORKSPACE_MEMBERS = '/workspace/{workspaceId}/members',
  SUBSCRIPTION_STATUS = '/workspace/{workspaceId}/subscription',
  UPDATE_WORKSPACE_CC_EMAILS = '/workspace/{workspaceId}/ccEmails',
  WATERMARK_LOAD = '/workspace/{workspaceId}/assets/watermark/{watermarkId}',
  WATERMARK_UPLOAD = '/workspace/{workspaceId}/watermark',
  LEAVE_WORKSPACE = '/workspace/{workspaceId}/leave',
  DELETE_WORKSPACE = '/workspace/{workspaceId}',
}

export interface InvitationPayload {
  workspaceId: string
  invitationId: string
}

interface ChangeWorkspaceMemberRolePayload {
  userId: string
  role: WorkspaceRole
  seatType: WorkspaceSeatType
  workspaceId: string
  canOrder?: boolean
}

interface CreateWorkspacePayload {
  workspaceName: string
}

interface RemoveAndTransferMemberPayload {
  workspaceId: string
  userToRemoveEmail: string
  transferToUserEmail: string
  reason?: string
}

// QUERIES

// This query is not used now, but should be back in use in future when BE endpoint is fixed, see MR https://gitlab.com/bkbn/frontend-platform/-/merge_requests/780
// where we started using different endpoint due to issues with endpoint /workspace/{workspaceId}/subscription returning
// 402 error: Payment Required if users' subscription are canceled.
export function useSubscriptionStatus(workspaceId: string) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: [EntityKeys.SUBSCRIPTION_STATUS, QueryType.GET, { workspaceId }],
    queryFn: () =>
      api.get<SubscriptionStatusDTO>(Endpoints.SUBSCRIPTION_STATUS, { workspaceId }, false, {
        headers: {
          Accept: RoleMimetype.CLIENT, // TODO: Fix on BE so we can send proper accept header
        },
      }),
  })
}

export function useGetWorkspaceMembers(workspaceId: string) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: [EntityKeys.WORKSPACE, QueryType.LIST, { workspaceId }],
    queryFn: () => api.get<WorkspaceMemberProfile[]>(Endpoints.GET_WORKSPACE_MEMBERS, { workspaceId }, false),
    enabled: !!workspaceId,
  })
}

// MUTATIONS

export function useUpdateWorkspaceName() {
  const api = useAPI<Endpoints>()

  return getMutation<void, { workspaceId: string; title: string }>(
    ({ workspaceId, title }) => api.patch(Endpoints.UPDATE_WORKSPACE_NAME, { workspaceId }, { title }, false),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    }
  )
}

export function useAddWorkspaceMembers() {
  const api = useAPI<Endpoints>()

  return getMutation<WorkspaceAddMembersResultDTO, { workspaceId: string; members: UserAsMemberUsingEmailDTO[] }>(
    ({ workspaceId, members }) => api.post(Endpoints.ADD_WORKSPACE_MEMBERS, { workspaceId }, [...members], false),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    }
  )
}

export function useRemoveAndTransferWorkspaceMember() {
  const api = useAPI<Endpoints>()

  return getMutation<{}, RemoveAndTransferMemberPayload>(
    ({ workspaceId, userToRemoveEmail, transferToUserEmail, reason }) =>
      api.delete(Endpoints.REMOVE_AND_TRANSFER_MEMBER, { workspaceId }, false, {
        data: {
          userToRemoveEmail,
          transferToUserEmail,
          ...(reason && { reason }),
        },
      }),
    (client, { workspaceId }) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
      client.invalidateQueries({ queryKey: [EntityKeys.WORKSPACE, QueryType.LIST, { workspaceId }] })
    }
  )
}

/**
 * under FF allowPendingInvitations
 */
export function useRevokeWorkspaceInvitation() {
  const api = useAPI<Endpoints>()

  return getMutation<{}, InvitationPayload>(
    ({ workspaceId, invitationId }) => api.delete(Endpoints.WORKSPACE_INVITATION, { workspaceId, invitationId }, false),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    }
  )
}

export function useLeaveWorkspace() {
  const api = useAPI<Endpoints>()

  return getMutation<{}, { workspaceId: string }>(
    ({ workspaceId }) => api.delete(Endpoints.LEAVE_WORKSPACE, { workspaceId }, false),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    }
  )
}

export function useDeleteWorkspace() {
  const api = useAPI<Endpoints>()

  return getMutation<{}, { workspaceId: string }>(
    ({ workspaceId }) => api.delete(Endpoints.DELETE_WORKSPACE, { workspaceId }, false),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    }
  )
}

export function useChangeWorkspaceMemberRole() {
  const api = useAPI<Endpoints>()

  return getMutation<{}, ChangeWorkspaceMemberRolePayload>(
    ({ workspaceId, userId, role, seatType, canOrder }) =>
      api.put(
        Endpoints.CHANGE_WORKSPACE_MEMBER_ROLE,
        { workspaceId },
        {
          userId,
          role,
          seatType,
          ...(canOrder !== undefined && { canOrder }),
        },
        false
      ),
    (client, { workspaceId }) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
      client.invalidateQueries({ queryKey: [EntityKeys.WORKSPACE, QueryType.LIST, { workspaceId }] })
    }
  )
}

/**
 * under FF allowPendingInvitations
 */
export function useResendWorkspaceInvitation() {
  const api = useAPI<Endpoints>()

  return getMutation<{}, InvitationPayload>(
    ({ workspaceId, invitationId }) => api.post(Endpoints.RESEND_WORKSPACE_INVITATION, { workspaceId, invitationId }, {}, false),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    }
  )
}

/**
 * under FF allowPendingInvitations
 */
export function useAcceptWorkspaceInvitation() {
  const api = useAPI<Endpoints>()

  return getMutation<{}, InvitationPayload>(
    ({ workspaceId, invitationId }) => api.post(Endpoints.WORKSPACE_INVITATION, { workspaceId, invitationId }, {}, false),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    }
  )
}

/**
 * Creates a new workspace
 */
export function useCreateWorkspace() {
  const api = useAPI<Endpoints>()

  return getMutation<NewWorkspaceDTO, CreateWorkspacePayload>(
    ({ workspaceName }) => api.post(Endpoints.CREATE_WORKSPACE, {}, { name: workspaceName }, false),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    }
  )
}

/** Mutation for updating workspace CC emails. */
export function useUpdateWorkspaceCcEmails() {
  const api = useAPI<Endpoints>()

  return getMutation<void, { workspaceId: string; ccEmails: string[] }>(
    ({ workspaceId, ccEmails }) => api.put(Endpoints.UPDATE_WORKSPACE_CC_EMAILS, { workspaceId }, ccEmails, false),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    }
  )
}
